/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { ProactiveChatNames } from '../../helpers/constants';
import { IChatContext, IDateRangeContext, TContextProvider } from '../../../interfaces';
import { IMsdyn365CsChatConnectorViewProps } from '../../msdyn365-cs-chat-connector';
import ProactiveChatTrigger from './proactive-chat-trigger';

/**
 * DateRangeTrigger class.
 * Proactive chat is triggered if current date is in preset date boundaries (config.proactiveOnDateRangeStart, config.proactiveOnDateRangeEnd).
 */
export default class DateRangeTrigger extends ProactiveChatTrigger {
    private start?: Date;
    private end?: Date;

    /**
     * Class constructor.
     * @param {IMsdyn365CsChatConnectorViewProps} props The chat connector view properties.
     * @param {IChatContext} context The context.
     */
    constructor(props: IMsdyn365CsChatConnectorViewProps, context: IChatContext) {
        super(props, context);
        this.start = this._toDate(this.props.config.proactiveOnDateRangeStart);
        this.end = this._toDate(this.props.config.proactiveOnDateRangeEnd);

        this.props.context.telemetry.debug(
            `[${DateRangeTrigger.name}] Proactive chat initialized with startDate: '${this.start}'; max: '${this.end}'.`
        );
    }

    /**
     * Validates whether the condition(s) is met to trigger proactive chat: current time is within the start and end time.
     * @returns {Promise<boolean>} True if condition is met. Otherwise, false.
     */
    public async validate(): Promise<boolean> {
        const now = new Date();
        now.setHours(0, 0, 0, 0);

        let retVal: boolean = false;
        if (this.start) {
            if (this.end) {
                retVal = this.start.getTime() <= now.getTime() && now.getTime() <= this.end.getTime();
            } else {
                retVal = this.start.getTime() <= now.getTime();
            }
        } else if (this.end) {
            retVal = now.getTime() <= this.end.getTime();
        }

        this.props.context.telemetry.debug(
            `[${DateRangeTrigger.name}] Validation result: '${retVal}'; Current date used to compare: '${now}'.`
        );
        return retVal;
    }

    /**
     * Gets the context.
     * @returns {TContextProvider} The context.
     */
    public getContextProvider(): TContextProvider {
        return (): IDateRangeContext => {
            return {
                ...this.context,
                IsProactiveChat: {
                    value: 'True',
                    isDisplayable: true
                },
                ProactiveType: {
                    value: ProactiveChatNames.DateRange,
                    isDisplayable: true
                },
                ProactiveFromDate: {
                    value: this.start ? this.start.toUTCString() : '',
                    isDisplayable: true
                },
                ProactiveToDate: {
                    value: this.end ? this.end.toUTCString() : '',
                    isDisplayable: true
                }
            };
        };
    }

    /**
     * Gets the message to be displayed when proactive chat is triggered.
     * @returns {string} The message to be displayed when proactive chat is triggered.
     */
    public getMessage(): string {
        return this.props.config.proactiveOnDateRangeMessage || this.props.config.proactiveDefaultMessage;
    }

    /**
     * Converts string(us-en) to date.
     * @param {string} [date] The date to convert (mm/dd/yyyy).
     * @returns {Date | undefined} The date or undefined.
     */
    private _toDate(date?: string): Date | undefined {
        if (date) {
            const dateParts = date.split('/');
            return new Date(+dateParts[2], +dateParts[1] - 1, +dateParts[0]);
        } else {
            return undefined;
        }
    }
}
